import { Divider, Flex, Input, Select, Typography } from "antd"
import VSpace from "../../common/VSpace"
import SettingsCard from "../SettingsCard"
import { GeneralInvoicingSettings, InvoicingSettings } from "../../../domain/invoicing/settings/invoicingSettings"
import { useEffect, useState } from "react"
import { areGeneralInvoicingSettingsUpdated, getGeneralInvoicingSettings } from "./invoicingSettingsModel"
import { getForCountry } from "../../../services/statesService"
import { getPrimaryCountries } from "../../../services/countriesService"
import GooglePlacesAutoCompleteInput from "../../common/GooglePlacesAutoCompleteInput"
import { PlaceData } from "../../common/googlePlacesAutoCompleteModel"
import { useUpdateGeneralSettingsMutation } from "../../../domain/invoicing/settings/invoicingSettingsQueries"

type Props = {
  serverSettings: InvoicingSettings
}

const GeneralInvoicingSettingsCard = ({serverSettings}: Props) => {
  const [settings, setSettings] = useState<GeneralInvoicingSettings>(getGeneralInvoicingSettings(serverSettings))
  const isUpdateDisabled = !areGeneralInvoicingSettingsUpdated(settings, serverSettings)
  const mutation = useUpdateGeneralSettingsMutation()

  useEffect(() => {
    setSettings(getGeneralInvoicingSettings(serverSettings))
  }, [serverSettings])

  const dueDateOptions = [
    { label: 'Due immediately', value: 0 },
    { label: 'Due in 15 days', value: 15 },
    { label: 'Due in 30 days', value: 30 },
    { label: 'Due in 45 days', value: 45 },
    { label: 'Due in 90 days', value: 90 }
  ]
  const stateOptions = buildStateOptions(settings.invoicingAddress.countryCode)

  const handlePlaceData = (data: PlaceData) => {
    setSettings({
      ...settings,
      invoicingAddress: {
        address1: data.address1,
        city: data.city,
        zip: data.zip,
        countryCode: data.countryCode,
        state: data.stateShort
      }
    })
  }

  return <SettingsCard
    title="General Invoicing Settings"
    description=""
    onUpdate={() => mutation.mutate(settings)}
    isUpdating={mutation.isLoading}
    updateDisabled={isUpdateDisabled || mutation.isLoading}
  >
    <VSpace>
      <Flex justify="space-between" gap={16}>
        <VSpace>
          <span>Default Invoice Due Date</span>
          <Select
            style={{width: '100%'}}
            options={dueDateOptions}
            value={settings.defaultDueInDays}
            onChange={defaultDueInDays => setSettings({...settings, defaultDueInDays})}
          />
          <Typography.Text type="secondary">By default, invoices will be due {settings.defaultDueInDays} days after they are finalized or sent.</Typography.Text>
        </VSpace>
        <VSpace>
          <span>Invoicing Support Email Address</span>
          <Input
            value={settings.supportEmail}
            onChange={e => setSettings({...settings, supportEmail: e.target.value})}
            maxLength={100}
          />
          <Typography.Text type="secondary">Enter the email address customers can use for invoice and payment questions.</Typography.Text>
        </VSpace>
      </Flex>
      <Divider plain>Invoicing Address</Divider>
      <Typography.Text type="secondary">Enter the business address shown on invoices and payment screens. This is also where customers will send mailed checks.</Typography.Text>
      <VSpace>
        <span>Street Address</span>
        <GooglePlacesAutoCompleteInput
          onPlaceData={handlePlaceData}
          apiKey={window.env.GOOGLE_API_KEY}
          value={settings.invoicingAddress.address1}
          onChange={e => setSettings({...settings, invoicingAddress: {...settings.invoicingAddress, address1: e.target.value}})}
          maxLength={150}
        />
      </VSpace>
      <Flex justify="space-between" gap={16}>
        <div style={{flex: '3'}}>
          <VSpace>
            <span>Country</span>
            <Select
              style={{width: '100%'}}
              options={countryOptions}
              value={settings.invoicingAddress.countryCode}
              onChange={countryCode => setSettings({...settings, invoicingAddress: {...settings.invoicingAddress, countryCode: countryCode}})}
            />
          </VSpace>
        </div>
        <div style={{flex: '3'}}>
          <VSpace>
            <span>City</span>
            <Input
              value={settings.invoicingAddress.city}
              onChange={e => setSettings({...settings, invoicingAddress: {...settings.invoicingAddress, city: e.target.value}})}
              maxLength={35}
            />
          </VSpace>
        </div>
        <div style={{flex: '1'}}>
          <VSpace>
            <span>State</span>
            {stateOptions.length > 0
              ? <Select
                  style={{width: '100%'}}
                  options={stateOptions}
                  value={settings.invoicingAddress.state}
                  onChange={stateCode => setSettings({...settings, invoicingAddress: {...settings.invoicingAddress, state: stateCode}})}
                />
              : <Input
                  value={settings.invoicingAddress.state}
                  onChange={e => setSettings({...settings, invoicingAddress: {...settings.invoicingAddress, state: e.target.value}})}
                  maxLength={35}
                />
            }
          </VSpace>
        </div>
        <div style={{flex: '1'}}>
          <VSpace>
            <span>Zip Code</span>
            <Input
              value={settings.invoicingAddress.zip}
              onChange={e => setSettings({...settings, invoicingAddress: {...settings.invoicingAddress, zip: e.target.value}})}
              maxLength={10}
            />
          </VSpace>
        </div>
      </Flex>
    </VSpace>
  </SettingsCard>
}

export default GeneralInvoicingSettingsCard

const countryOptions = getPrimaryCountries().map((country) => ({
  label: country.name,
  value: country.code
}))

const buildStateOptions = (countryCode: string) => {
  const states = getForCountry(countryCode) ?? []
  return states.map((state) => ({
    label: state.name,
    value: state.code
  }))
}